import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  GoalListItem,
  GoalOption,
  GoalRequest,
  GoalSectorListItem,
  GoalSectorOption,
  GoalSectorRequest,
  GoalSectorTreeNode,
  GoalSubSectorListItem,
  GoalSubSectorOption,
  GoalSubSectorRequest,
} from './budgetGoalTypes';

/** The sub-sector list files by its parent sector. */
export interface GoalSubSectorListQuery extends ListQuery {
  sector?: string;
}

/** The goal list files by sector AND by sub-sector. */
export interface GoalListQuery extends ListQuery {
  sector?: string;
  subSector?: string;
}

/*
  Every facet parameter is sent whenever the KEY is present, empty string included — an empty
  value is the reader saying "none of these", which matches no row. A truthiness check here
  would drop it and the server would read the axis as unmentioned, which is the opposite
  filter. See FacetQuery on the backend.
*/
function toQueryString(query: GoalListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);
  if (query.sector !== undefined) params.set('sector', query.sector);
  if (query.subSector !== undefined) params.set('subSector', query.subSector);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * The goal catalog's three rungs.
 *
 * Three endpoints behind ONE permission key set (`budget-goals.*`) — maintaining the hierarchy
 * is one administrative job. The routes are flat, like every other catalog here: it is the
 * frontend PATH that nests under the Settings menu, because the breadcrumb is the back-nav.
 */
export const goalSectorsApi = {
  clear: () => api<number>('/api/goal-sectors/clear', { method: 'POST' }),

  list: (query: ListQuery, signal?: AbortSignal) =>
    api<PagedResult<GoalSectorListItem>>(`/api/goal-sectors${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<GoalSectorListItem>(`/api/goal-sectors/${id}`, { signal }),

  /** Active sectors for the sub-sector form's parent picker. Unpaged. */
  options: (signal?: AbortSignal) =>
    api<GoalSectorOption[]>('/api/goal-sectors/options', { signal }),

  /**
   * The whole catalog nested — sector, sub-sector, goal — for the Goal Setting tab.
   *
   * `includeInactive` keeps deleted rows in the result so the tab can mark them and offer
   * Restore: a row nothing lists is a row nobody can put back.
   */
  tree: (includeInactive = false, signal?: AbortSignal) =>
    api<GoalSectorTreeNode[]>(
      `/api/goal-sectors/tree${includeInactive ? '?includeInactive=true' : ''}`,
      { signal },
    ),

  create: (request: GoalSectorRequest) =>
    api<GoalSectorListItem>('/api/goal-sectors', { method: 'POST', body: request }),

  update: (id: string, request: GoalSectorRequest) =>
    api<GoalSectorListItem>(`/api/goal-sectors/${id}`, { method: 'PUT', body: request }),

  deactivate: (id: string) => api<void>(`/api/goal-sectors/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/goal-sectors/${id}/restore`, { method: 'POST' }),
};

export const goalSubSectorsApi = {
  clear: () => api<number>('/api/goal-sub-sectors/clear', { method: 'POST' }),

  list: (query: GoalSubSectorListQuery, signal?: AbortSignal) =>
    api<PagedResult<GoalSubSectorListItem>>(`/api/goal-sub-sectors${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) =>
    api<GoalSubSectorListItem>(`/api/goal-sub-sectors/${id}`, { signal }),

  /** Active sub-sectors for the goal form's parent picker. Unpaged. */
  options: (signal?: AbortSignal) =>
    api<GoalSubSectorOption[]>('/api/goal-sub-sectors/options', { signal }),

  create: (request: GoalSubSectorRequest) =>
    api<GoalSubSectorListItem>('/api/goal-sub-sectors', { method: 'POST', body: request }),

  update: (id: string, request: GoalSubSectorRequest) =>
    api<GoalSubSectorListItem>(`/api/goal-sub-sectors/${id}`, { method: 'PUT', body: request }),

  deactivate: (id: string) => api<void>(`/api/goal-sub-sectors/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/goal-sub-sectors/${id}/restore`, { method: 'POST' }),
};

export const goalsApi = {
  clear: () => api<number>('/api/goals/clear', { method: 'POST' }),

  list: (query: GoalListQuery, signal?: AbortSignal) =>
    api<PagedResult<GoalListItem>>(`/api/goals${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<GoalListItem>(`/api/goals/${id}`, { signal }),

  /**
   * Active goals for the budget plan's Goal Setting and the PPA's alignment picker. Unpaged.
   *
   * Only goals whose sub-sector AND sector are both active: one under a retired rung is a goal
   * nobody can file correctly.
   */
  options: (signal?: AbortSignal) => api<GoalOption[]>('/api/goals/options', { signal }),

  create: (request: GoalRequest) => api<GoalListItem>('/api/goals', { method: 'POST', body: request }),

  update: (id: string, request: GoalRequest) =>
    api<GoalListItem>(`/api/goals/${id}`, { method: 'PUT', body: request }),

  deactivate: (id: string) => api<void>(`/api/goals/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/goals/${id}/restore`, { method: 'POST' }),
};
